import { AccountInfo } from "@azure/msal-browser";
import { getHermesEnvironment } from "@env/envrionment";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AccountStoreState {
  account: AccountInfo | undefined;
  setAccount(account: AccountInfo): void;
  // This lets the user choose an account to try to log in to
  // if they sign out or their refresh token expires. It's mainly
  // used as a workaround for users who only have access to Hermes
  // via a guest account.
  preferredAccount: AccountInfo | undefined;
  setPreferredAccount(account: AccountInfo | undefined): void;
}

/**
 * Global state to select an acount other than the active account in MSAL.
 * Useful when explicitly making requests to other tenants (ex. MS Graph)
 */
const useAccountStore = create<AccountStoreState>()(
  persist(
    (set) => ({
      account: getAccountStoreFromLocalStorage()?.account,
      setAccount: (a) => set((state) => ({ ...state, account: a })),
      preferredAccount: getAccountStoreFromLocalStorage()?.preferredAccount,
      setPreferredAccount: (a) =>
        set((state) => ({ ...state, preferredAccount: a })),
    }),
    {
      name: `hermes.${getHermesEnvironment().name}.accountStore`,
    }
  )
);

/**
 * Pure function to get the account store from localStorage
 * This can be used outside of React components (e.g., in auth.ts)
 */
export function getAccountStoreFromLocalStorage():
  | AccountStoreState
  | undefined {
  try {
    const persistedState = localStorage.getItem(
      `hermes.${getHermesEnvironment().name}.accountStore`
    );

    if (!persistedState) {
      console.warn("No account store found in localStorage");
      return undefined;
    }

    const parsed = JSON.parse(persistedState);
    return parsed.state;
  } catch (e) {
    // If parsing fails, return undefined
    console.warn("Failed to parse account store from localStorage:", e);
  }
  return undefined;
}

export default useAccountStore;
